/**
 * Bulk archive / restore handlers for DocTypeListPageShell's SelectionFloatingBar.
 *
 * Extracted from DocTypeListPageShell (file-size split, behaviour unchanged).
 */

import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";
import type { RowSelectionState } from "@tanstack/react-table";
import { toast } from "sonner";
import { frappeMethod } from "@/lib/erp/utils";

type BulkDocMethod =
  | "zaviago_backend.api.archive.archive_doc"
  | "zaviago_backend.api.archive.restore_doc";

export function useShellBulkActions({
  doctype,
  rowSelection,
  setRowSelection,
  refetch,
}: {
  doctype: string;
  rowSelection: RowSelectionState;
  setRowSelection: (next: RowSelectionState) => void;
  refetch?: () => unknown;
}) {
  const queryClient = useQueryClient();

  const runBulk = useCallback(
    async (method: BulkDocMethod, verb: "archive" | "restore", pastVerb: string) => {
      // Use rowSelection keys directly — they ARE the doc names (via getRowId in useReactTable).
      // table.getSelectedRowModel().rows only returns top-level rows, which drops selected
      // variant/sub-rows when the parent isn't also selected.
      const names = Object.keys(rowSelection);
      if (names.length === 0) return;
      const results = await Promise.allSettled(
        names.map((name) =>
          frappeMethod<{ success: boolean; message: string }>(method, { doctype, name }),
        ),
      );
      const failed = results.filter((r) => r.status === "rejected").length;
      if (failed > 0) {
        toast.error(`${failed} of ${names.length} items failed to ${verb}`);
      } else {
        toast.success(`${names.length} items ${pastVerb}`);
      }
      setRowSelection({});
      refetch?.();
      queryClient.invalidateQueries({ queryKey: ["archived-count"] });
    },
    [doctype, rowSelection, setRowSelection, refetch, queryClient],
  );

  const handleBulkArchive = useCallback(
    () => runBulk("zaviago_backend.api.archive.archive_doc", "archive", "archived"),
    [runBulk],
  );

  const handleBulkRestore = useCallback(
    () => runBulk("zaviago_backend.api.archive.restore_doc", "restore", "restored"),
    [runBulk],
  );

  return { handleBulkArchive, handleBulkRestore };
}
